import axios, { AxiosResponse } from 'axios';
import { apiService } from './ApiService';

export interface VehicleTags {
  make?: string | null;
  models?: string[];
  year_from?: number | null;
  year_to?: number | null;
}

/** PATCH payload for document details — only the fields sent are updated. */
export interface DocumentDetails extends VehicleTags {
  title?: string | null;
  doc_type?: string | null;
}

/** One reference document — a client document that grounds Cata's answers
 *  (see /api/enrichment/sources on the backend). ``injected`` sources are
 *  extracted to JSON and matched into order extra_data; ``attach`` sources
 *  keep the PDF in storage and are attached whole to answers for matching
 *  vehicles (routed by vehicle tags — no chunking, no embeddings). */
export interface EnrichmentSource {
  id: number;
  name: string;
  status: 'syncing' | 'active' | 'error' | 'inactive';
  /** Consumption mode — assigned by extracted size, absent on legacy rows (= injected) */
  mode?: 'injected' | 'attach';
  /** Uploaded file size in bytes (when known) */
  fileSize?: number | null;
  /** Clean human title inferred from the cover (falls back to name) */
  displayTitle?: string | null;
  /** owner_manual | maintenance | warranty | safety | spec_sheet | other */
  docType?: string | null;
  /** PDF page count (attach documents) */
  pageCount?: number | null;
  /** Vehicle coverage inferred at ingest — admin-correctable */
  vehicleTags?: VehicleTags | null;
  fileName?: string | null;
  version: number;
  topLevelKeys?: number | null;
  error?: string | null;
  /** Chunk progress while extracting, e.g. "3/12" (null when not syncing) */
  progress?: string | null;
  /** Admin-provided parsing guidance, included in every extraction call */
  context?: string | null;
  /** Owning org (null = shared source assigned via shared_organization_ids) */
  organization_id?: number | null;
  /** Orgs a shared source is assigned to */
  shared_organization_ids?: number[];
  created_at?: string;
  updated_at?: string;
}

export interface MatchedReferenceDoc {
  source_id: number;
  title: string;
  doc_type: string;
  page_count?: number | null;
  chunks_ready: boolean;
}

export interface MatchedReferenceDocsDebug {
  enabled: boolean;
  vehicle_filter: { makes: string[]; model_terms: string[]; year: number | null };
  matched: MatchedReferenceDoc[];
}

class EnrichmentService {
  public async listSources(): Promise<AxiosResponse<{ sources: EnrichmentSource[] }>> {
    return apiService.get('/api/enrichment/sources');
  }

  /** Admin debug: the reference documents the agent would use for this
   *  order's vehicle (same matcher QA runs). */
  public async getMatchedReferenceDocs(orderId: string | number): Promise<AxiosResponse<MatchedReferenceDocsDebug>> {
    return apiService.get(`/api/reference-docs/debug/matched?order_id=${encodeURIComponent(String(orderId))}`);
  }

  public async uploadSource(file: File, name: string, context?: string): Promise<AxiosResponse<EnrichmentSource>> {
    const form = new FormData();
    form.append('file', file);
    form.append('name', name);
    if (context?.trim()) form.append('context', context.trim());
    return apiService.post('/api/enrichment/sources', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  }

  public async getJson(sourceId: number): Promise<AxiosResponse<{ version: number; data: Record<string, any> }>> {
    return apiService.get(`/api/enrichment/sources/${sourceId}/json`);
  }

  public async saveJson(sourceId: number, data: Record<string, any>): Promise<AxiosResponse<EnrichmentSource>> {
    return apiService.put(`/api/enrichment/sources/${sourceId}/json`, { data });
  }

  public async reExtract(sourceId: number): Promise<AxiosResponse<EnrichmentSource>> {
    return apiService.post(`/api/enrichment/sources/${sourceId}/re-extract`);
  }

  public async deleteSource(sourceId: number): Promise<AxiosResponse<{ deleted: number }>> {
    return apiService.delete(`/api/enrichment/sources/${sourceId}`);
  }

  // ── Large files: direct-to-GCS via signed URL (bypasses Cloud Run's 32MiB cap) ──

  /** PUT a file straight to a GCS signed URL. Bare axios (NOT apiService) so
   *  our Authorization header/interceptors never reach GCS — the signed URL
   *  carries its own auth, and extra headers break the signature. */
  private async putToSignedUrl(url: string, file: File, contentType: string, onProgress?: (p: number) => void): Promise<void> {
    await axios.put(url, file, {
      headers: { 'Content-Type': contentType },
      timeout: 0, // no client timeout — GB files can take a while
      transformRequest: [(d) => d], // don't let axios stringify the File
      onUploadProgress: onProgress
        ? (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)); }
        : undefined,
    });
  }

  /** Mint a signed URL, PUT the file to GCS, then register the source.
   *  Small extractable files still go through extraction server-side; large
   *  ones are served as attached documents once the metadata pass finishes. */
  public async uploadSourceViaSignedUrl(
    file: File, name: string, context?: string, onProgress?: (p: number) => void,
  ): Promise<AxiosResponse<EnrichmentSource>> {
    const contentType = file.type || 'application/octet-stream';
    const urlRes = await apiService.post<{ upload_url: string; gcs_path: string }>(
      '/api/reference-docs/uploads',
      { filename: file.name, content_type: contentType, size: file.size },
    );
    await this.putToSignedUrl(urlRes.data.upload_url, file, contentType, onProgress);
    return apiService.post('/api/reference-docs/sources', {
      gcs_path: urlRes.data.gcs_path,
      name,
      file_name: file.name,
      file_size: file.size,
      context: context?.trim() || undefined,
    }, { timeout: 300000 });
  }

  /** Admin/shared counterpart of uploadSourceViaSignedUrl. */
  public async adminUploadSourceViaSignedUrl(
    file: File, name: string, organizationIds: number[], context?: string, onProgress?: (p: number) => void,
  ): Promise<AxiosResponse<EnrichmentSource>> {
    const contentType = file.type || 'application/octet-stream';
    const urlRes = await apiService.post<{ upload_url: string; gcs_path: string }>(
      '/api/reference-docs/admin/uploads',
      { filename: file.name, content_type: contentType, size: file.size },
    );
    await this.putToSignedUrl(urlRes.data.upload_url, file, contentType, onProgress);
    return apiService.post('/api/reference-docs/admin/sources', {
      gcs_path: urlRes.data.gcs_path,
      name,
      file_name: file.name,
      file_size: file.size,
      context: context?.trim() || undefined,
      organization_ids: organizationIds,
    }, { timeout: 300000 });
  }

  /** Correct an attached document's details (coverage, title, type) —
   *  effective on the next question, no re-processing. */
  public async updateVehicleTags(
    sourceId: number, tags: DocumentDetails, isAdmin: boolean,
  ): Promise<AxiosResponse<EnrichmentSource>> {
    const base = isAdmin ? '/api/reference-docs/admin/sources' : '/api/reference-docs/sources';
    return apiService.patch(`${base}/${sourceId}/tags`, tags);
  }

  // ── Platform-admin: shared sources assigned to multiple organisations ──

  public async adminListSources(): Promise<AxiosResponse<{ sources: EnrichmentSource[] }>> {
    return apiService.get('/api/enrichment/admin/sources');
  }

  public async adminUploadSource(
    file: File, name: string, organizationIds: number[], context?: string,
  ): Promise<AxiosResponse<EnrichmentSource>> {
    const form = new FormData();
    form.append('file', file);
    form.append('name', name);
    form.append('organization_ids', JSON.stringify(organizationIds));
    if (context?.trim()) form.append('context', context.trim());
    return apiService.post('/api/enrichment/admin/sources', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  }

  public async adminSetOrganizations(
    sourceId: number, organizationIds: number[],
  ): Promise<AxiosResponse<EnrichmentSource>> {
    return apiService.patch(`/api/enrichment/admin/sources/${sourceId}/organizations`, {
      organization_ids: organizationIds,
    });
  }

  public async adminGetJson(sourceId: number): Promise<AxiosResponse<{ version: number; data: Record<string, any> }>> {
    return apiService.get(`/api/enrichment/admin/sources/${sourceId}/json`);
  }

  public async adminSaveJson(sourceId: number, data: Record<string, any>): Promise<AxiosResponse<EnrichmentSource>> {
    return apiService.put(`/api/enrichment/admin/sources/${sourceId}/json`, { data });
  }

  public async adminReExtract(sourceId: number): Promise<AxiosResponse<EnrichmentSource>> {
    return apiService.post(`/api/enrichment/admin/sources/${sourceId}/re-extract`);
  }

  public async adminDeleteSource(sourceId: number): Promise<AxiosResponse<{ deleted: number }>> {
    return apiService.delete(`/api/enrichment/admin/sources/${sourceId}`);
  }

  // ── Extraction prompt (platform-admin, editable body + fixed contract) ──

  public async adminGetExtractionPrompt(): Promise<AxiosResponse<{
    body: string; is_default: boolean; default_body: string; contract: string;
  }>> {
    return apiService.get('/api/enrichment/admin/extraction-prompt');
  }

  /** Empty body resets to the code default. Affects future extractions only. */
  public async adminSetExtractionPrompt(body: string): Promise<AxiosResponse<any>> {
    return apiService.put('/api/enrichment/admin/extraction-prompt', { body });
  }
}

export const enrichmentService = new EnrichmentService();
